import { Type, type Static } from "@sinclair/typebox";

export const OperationsQuerySchema = Type.Object(
  {
    search: Type.Optional(Type.String({ maxLength: 200 })),
    code: Type.Optional(Type.String({ maxLength: 80 })),
    status: Type.Optional(Type.String({ maxLength: 40 })),
    kind: Type.Optional(Type.String({ maxLength: 40 })),
    administratorId: Type.Optional(Type.String({ format: "uuid" })),
    administratorAssignment: Type.Optional(
      Type.Union([Type.Literal("assigned"), Type.Literal("unassigned")]),
    ),
    administratorClientId: Type.Optional(Type.String({ format: "uuid" })),
    condominiumId: Type.Optional(Type.String({ format: "uuid" })),
    workStatus: Type.Optional(
      Type.Union([
        Type.Literal("open"),
        Type.Literal("in-progress"),
        Type.Literal("closed"),
        Type.Literal("work-completed"),
        Type.Literal("recovery-x"),
      ]),
    ),
    postWorkStatus: Type.Optional(
      Type.Union([
        Type.Literal("to-invoice"),
        Type.Literal("report"),
        Type.Literal("to-collect"),
        Type.Literal("closed"),
      ]),
    ),
    planningStatus: Type.Optional(
      Type.Union([Type.Literal("planned"), Type.Literal("to-plan")]),
    ),
    billingStatus: Type.Optional(
      Type.Union([
        Type.Literal("not-invoiced"),
        Type.Literal("intervention-report"),
        Type.Literal("invoiced"),
      ]),
    ),
    paymentStatus: Type.Optional(
      Type.Union([
        Type.Literal("unpaid"),
        Type.Literal("not-invoiced"),
        Type.Literal("verification-required"),
        Type.Literal("to-collect"),
        Type.Literal("partial"),
        Type.Literal("collected"),
        Type.Literal("overdue"),
      ]),
    ),
    registerScope: Type.Optional(
      Type.Union([
        Type.Literal("active"),
        Type.Literal("closed-archive"),
        Type.Literal("merged"),
        Type.Literal("all"),
      ]),
    ),
    sortBy: Type.Optional(
      Type.Union([
        Type.Literal("date"),
        Type.Literal("amount"),
        Type.Literal("profit"),
        Type.Literal("code"),
      ]),
    ),
    cursor: Type.Optional(Type.String({ minLength: 1, maxLength: 512 })),
    limit: Type.Optional(
      Type.Integer({ minimum: 1, maximum: 200, default: 50 }),
    ),
    managementYear: Type.Optional(
      Type.Integer({ minimum: 2000, maximum: 2200 }),
    ),
  },
  { additionalProperties: false },
);

export const PlanningWindowQuerySchema = Type.Object(
  {
    from: Type.Optional(Type.String({ format: "date" })),
    to: Type.Optional(Type.String({ format: "date" })),
  },
  { additionalProperties: false },
);

const PlanningWorkerKind = Type.Union([
  Type.Literal("employee"),
  Type.Literal("subcontract"),
]);
const PlanningItemKind = Type.Union([
  Type.Literal("assignment"),
  Type.Literal("absence"),
]);
const PlanningAbsenceType = Type.Union([
  Type.Literal("VACATION"),
  Type.Literal("SICKNESS"),
  Type.Literal("PAID_LEAVE"),
  Type.Literal("TRAINING"),
]);

export const OperationIdParamsSchema = Type.Object(
  { id: Type.String({ format: "uuid" }) },
  { additionalProperties: false },
);

export const JobEntryParamsSchema = Type.Object(
  {
    id: Type.String({ format: "uuid" }),
    entryId: Type.String({ format: "uuid" }),
  },
  { additionalProperties: false },
);

const NullableString = Type.Union([Type.String(), Type.Null()]);
const NullableUuid = Type.Union([Type.String({ format: "uuid" }), Type.Null()]);
const NullableMoney = Type.Union([
  Type.Number({ minimum: 0, maximum: 999999999999.99 }),
  Type.Null(),
]);
const NullableDate = Type.Union([Type.String({ format: "date" }), Type.Null()]);
const OperationId = Type.String({ format: "uuid" });

export const JobCodePreviewQuerySchema = Type.Object(
  {
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
    administratorId: Type.String({ format: "uuid" }),
    condominiumId: Type.String({ format: "uuid" }),
  },
  { additionalProperties: false },
);

export const JobOwnerBalanceQuerySchema = Type.Object(
  {
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
    quoteAmount: Type.Number({ exclusiveMinimum: 0 }),
  },
  { additionalProperties: false },
);

export const PlanningFeasibleUpdateSchema = Type.Object(
  {
    operationId: OperationId,
    version: Type.Integer({ minimum: 1 }),
    feasible: Type.Boolean(),
  },
  { additionalProperties: false },
);

export const PlanningItemCreateSchema = Type.Object(
  {
    operationId: OperationId,
    kind: PlanningItemKind,
    jobId: Type.Optional(Type.String({ format: "uuid" })),
    absenceType: Type.Optional(PlanningAbsenceType),
    employeeId: Type.Optional(NullableUuid),
    workerKind: PlanningWorkerKind,
    workerLabel: Type.Optional(
      Type.String({
        pattern: "^SUBAPPALTI(?: [1-9][0-9]{0,2})?$",
        maxLength: 14,
      }),
    ),
    workDate: Type.String({ format: "date" }),
    durationDays: Type.Integer({ minimum: 1, maximum: 120 }),
    warehouseCopyId: Type.Optional(Type.String({ format: "uuid" })),
    sourceAssignmentId: Type.Optional(Type.String({ format: "uuid" })),
    sourceVersion: Type.Optional(Type.Integer({ minimum: 1 })),
  },
  { additionalProperties: false },
);

export const PlanningBudgetPreviewSchema = Type.Object(
  {
    assignmentId: Type.String({ format: "uuid" }),
    version: Type.Integer({ minimum: 1 }),
    durationDays: Type.Integer({ minimum: 1, maximum: 120 }),
  },
  { additionalProperties: false },
);

export interface PlanningBudgetPreview {
  personDays: string;
  plannedHours: string;
  plannedLaborCost: string;
  supplierCost: string;
  forecastCost: string;
  budgetAmount: string | null;
  budgetExcess: string | null;
  expectedRevenue: string | null;
  estimatedProfit: string | null;
  marginPercent: string | null;
  missingWorkers: string[];
}

export const PlanningItemUpdateSchema = Type.Object(
  {
    operationId: OperationId,
    version: Type.Integer({ minimum: 1 }),
    kind: PlanningItemKind,
    employeeId: Type.Optional(NullableUuid),
    workerKind: PlanningWorkerKind,
    workerLabel: Type.Optional(
      Type.String({
        pattern: "^SUBAPPALTI(?: [1-9][0-9]{0,2})?$",
        maxLength: 14,
      }),
    ),
    workDate: Type.String({ format: "date" }),
    durationDays: Type.Integer({ minimum: 1, maximum: 120 }),
  },
  { additionalProperties: false },
);

export const PlanningPauseSchema = Type.Object(
  {
    operationId: OperationId,
    version: Type.Integer({ minimum: 1 }),
    action: Type.Union([Type.Literal("pause"), Type.Literal("resume")]),
    date: Type.String({ format: "date" }),
  },
  { additionalProperties: false },
);
export type PlanningPause = Static<typeof PlanningPauseSchema>;

export const PlanningItemDeleteQuerySchema = Type.Object(
  {
    operationId: OperationId,
    version: Type.Integer({ minimum: 1 }),
    kind: PlanningItemKind,
  },
  { additionalProperties: false },
);

export const PlanningClearSchema = Type.Object(
  {
    operationId: OperationId,
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
  },
  { additionalProperties: false },
);

export const PlanningWeekendUpdateSchema = Type.Object(
  {
    operationId: OperationId,
    employeeId: Type.String({ format: "uuid" }),
    workDate: Type.String({ format: "date" }),
    workValue: Type.Union([
      Type.Literal(0),
      Type.Literal(0.5),
      Type.Literal(1),
    ]),
  },
  { additionalProperties: false },
);

export const PlanningWarehouseCopyCreateSchema = Type.Object(
  {
    operationId: OperationId,
    sourceAssignmentId: Type.String({ format: "uuid" }),
    version: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);
const JobStatus = Type.Union([
  Type.Literal("draft"),
  Type.Literal("open"),
  Type.Literal("work-completed"),
  Type.Literal("to-invoice"),
  Type.Literal("invoiced"),
  Type.Literal("closed"),
  Type.Literal("archived"),
  Type.Literal("cancelled"),
]);
const JobWriteFields = {
  informationNotes: Type.Optional(
    Type.Union([Type.String({ maxLength: 20000 }), Type.Null()]),
  ),
  contactPhone: Type.Optional(
    Type.Union([Type.String({ maxLength: 80 }), Type.Null()]),
  ),
  title: Type.Optional(Type.String({ minLength: 1, maxLength: 240 })),
  description: Type.Optional(NullableString),
  clientId: Type.Optional(NullableUuid),
  condominiumId: Type.Optional(NullableUuid),
  administratorClientId: Type.Optional(NullableUuid),
  commercialContact: Type.Optional(NullableString),
  classification: Type.Optional(NullableString),
  openedOn: Type.Optional(NullableDate),
  expectedEndOn: Type.Optional(NullableDate),
  ownerEmployeeId: Type.Optional(NullableUuid),
  fiscalVatId: Type.Optional(
    Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
  ),
  fiscalVatPercent: Type.Optional(
    Type.Union([Type.Number({ minimum: 0, maximum: 100 }), Type.Null()]),
  ),
  fiscalWithholdingEnabled: Type.Optional(Type.Boolean()),
  quoteAmount: Type.Optional(NullableMoney),
  budgetAmount: Type.Optional(NullableMoney),
  expectedRevenue: Type.Optional(NullableMoney),
  commercialReservePercent: Type.Optional(
    Type.Number({ minimum: 0, maximum: 100, multipleOf: 0.01 }),
  ),
  commercialBaseAmount: Type.Optional(NullableMoney),
  receivedAmount: Type.Optional(NullableMoney),
  paymentDueDate: Type.Optional(NullableDate),
  measurementUnit: Type.Optional(NullableString),
  plannedQuantity: Type.Optional(NullableMoney),
  actualQuantity: Type.Optional(NullableMoney),
  plannedHours: Type.Optional(NullableMoney),
};

export const JobCreateSchema = Type.Object(
  {
    operationId: OperationId,
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
    title: Type.String({ minLength: 1, maxLength: 240 }),
    description: Type.Optional(NullableString),
    parentJobId: Type.Optional(NullableUuid),
    accountingMode: Type.Optional(Type.Literal("rollup")),
    subjobNumber: Type.Optional(Type.Integer({ minimum: 1, maximum: 9999 })),
    administratorId: Type.Optional(NullableUuid),
    condominiumId: Type.Optional(NullableUuid),
    openedOn: Type.Optional(NullableDate),
    expectedEndOn: Type.Optional(NullableDate),
    ownerEmployeeId: Type.Optional(NullableUuid),
    quoteAmount: Type.Optional(NullableMoney),
    budgetAmount: Type.Optional(NullableMoney),
    quoteUnavailable: Type.Optional(Type.Boolean()),
    measurementUnit: Type.Optional(NullableString),
    plannedQuantity: Type.Optional(NullableMoney),
    actualQuantity: Type.Optional(NullableMoney),
    plannedHours: Type.Optional(NullableMoney),
  },
  { additionalProperties: false },
);

export const ExtraSubjobConversionApplySchema = Type.Object(
  {
    operationId: OperationId,
    previewToken: Type.String({ pattern: "^[a-f0-9]{64}$" }),
  },
  { additionalProperties: false },
);
export type ExtraSubjobConversionApply = Static<
  typeof ExtraSubjobConversionApplySchema
>;

export const JobUpdateSchema = Type.Object(
  {
    operationId: OperationId,
    version: Type.Integer({ minimum: 1 }),
    ...JobWriteFields,
  },
  { additionalProperties: false, minProperties: 3 },
);

export const JobStatusTransitionSchema = Type.Object(
  {
    operationId: OperationId,
    version: Type.Integer({ minimum: 1 }),
    status: JobStatus,
    reason: Type.Optional(Type.String({ maxLength: 500 })),
  },
  { additionalProperties: false },
);

export const JobArchiveWithoutBillingSchema = Type.Object(
  {
    operationId: OperationId,
    version: Type.Integer({ minimum: 1 }),
    reason: Type.String({ minLength: 1, maxLength: 500, pattern: "\\S" }),
  },
  { additionalProperties: false },
);
export type JobArchiveWithoutBilling = Static<
  typeof JobArchiveWithoutBillingSchema
>;

export const JobTimeEntryCreateSchema = Type.Object(
  {
    recurringOccurrenceId: Type.Optional(OperationId),
    operationId: OperationId,
    employeeId: Type.String({ format: "uuid" }),
    workedOn: Type.String({ format: "date" }),
    hours: Type.Number({ exclusiveMinimum: 0, maximum: 24 }),
    hourlyCost: Type.Optional(Type.Number({ minimum: 0 })),
    activity: Type.Optional(NullableString),
    notes: Type.Optional(NullableString),
  },
  { additionalProperties: false },
);

export const JobTimeEntriesCreateSchema = Type.Object(
  {
    recurringOccurrenceId: Type.Optional(OperationId),
    operationId: OperationId,
    workedOn: Type.String({ format: "date" }),
    entries: Type.Array(
      Type.Object(
        {
          employeeId: Type.String({ format: "uuid" }),
          hours: Type.Number({ exclusiveMinimum: 0, maximum: 24 }),
        },
        { additionalProperties: false },
      ),
      { minItems: 1, maxItems: 50 },
    ),
    activity: Type.Optional(NullableString),
    notes: Type.Optional(NullableString),
  },
  { additionalProperties: false },
);

export const JobTimeEntryUpdateSchema = Type.Object(
  {
    ...JobTimeEntryCreateSchema.properties,
    version: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

export const JobCostCreateSchema = Type.Object(
  {
    withoutInvoice: Type.Optional(Type.Boolean()),
    recurringOccurrenceId: Type.Optional(OperationId),
    operationId: OperationId,
    incurredOn: Type.String({ format: "date" }),
    category: Type.String({ minLength: 1, maxLength: 120 }),
    description: Type.Optional(NullableString),
    amount: Type.Number({ minimum: 0 }),
    vatStatus: Type.Optional(
      Type.Union([
        Type.Literal("known"),
        Type.Literal("unknown"),
        Type.Literal("not-applicable"),
      ]),
    ),
    netAmount: Type.Optional(NullableMoney),
    vatAmount: Type.Optional(NullableMoney),
    supplierId: Type.Optional(NullableUuid),
    supplierName: Type.Optional(NullableString),
    subcontract: Type.Optional(Type.Boolean()),
  },
  { additionalProperties: false },
);

export const JobRecoveryCreateSchema = Type.Object(
  {
    operationId: OperationId,
    amount: Type.Number({ exclusiveMinimum: 0, maximum: 9999999999.99 }),
    occurredOn: Type.String({ format: "date" }),
    title: Type.Optional(NullableString),
    notes: Type.Optional(NullableString),
  },
  { additionalProperties: false },
);

export const JobRecoveryTransferSchema = Type.Object(
  {
    operationId: OperationId,
    targetJobId: Type.String({ format: "uuid" }),
    amount: Type.Number({ exclusiveMinimum: 0, maximum: 9999999999.99 }),
    occurredOn: Type.String({ format: "date" }),
    notes: Type.Optional(NullableString),
  },
  { additionalProperties: false },
);

export const JobRecoveryMergeSchema = Type.Object(
  {
    operationId: OperationId,
    targetRecoveryJobId: Type.String({ format: "uuid" }),
    occurredOn: Type.String({ format: "date" }),
    notes: Type.Optional(NullableString),
  },
  { additionalProperties: false },
);

export const JobMergeSchema = Type.Object(
  {
    operationId: OperationId,
    targetJobId: Type.String({ format: "uuid" }),
    sourceVersion: Type.Integer({ minimum: 1 }),
    targetVersion: Type.Integer({ minimum: 1 }),
    title: Type.String({ minLength: 1, maxLength: 240 }),
    description: Type.Optional(NullableString),
    reason: Type.String({ minLength: 3, maxLength: 1000 }),
  },
  { additionalProperties: false },
);

export const JobDeleteQuerySchema = Type.Object(
  {
    operationId: OperationId,
    version: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

export const JobEntryDeleteQuerySchema = JobDeleteQuerySchema;

const LifelineWriteFields = {
  managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
  condominiumId: Type.String({ format: "uuid" }),
  jobId: Type.Optional(NullableUuid),
  title: Type.Optional(Type.String({ maxLength: 240 })),
  building: Type.Optional(NullableString),
  coverage: Type.Optional(NullableString),
  responsible: Type.Optional(NullableString),
  lifecycleStatus: Type.String({ minLength: 1, maxLength: 80 }),
  openedOn: Type.Optional(NullableDate),
  manufacturer: Type.Optional(NullableString),
  model: Type.Optional(NullableString),
  plateCode: Type.Optional(NullableString),
  notes: Type.Optional(NullableString),
  usabilityStatus: Type.Union([
    Type.Literal("usable"),
    Type.Literal("restricted"),
    Type.Literal("not-usable"),
    Type.Literal("unknown"),
  ]),
  nextInterventionOn: Type.Optional(NullableDate),
  frequency: Type.Optional(NullableString),
  deadlineNotes: Type.Optional(NullableString),
  quoteAmount: Type.Optional(NullableMoney),
  budgetAmount: Type.Optional(NullableMoney),
  plannedHours: Type.Optional(NullableMoney),
  subscriptionPrice: Type.Optional(NullableMoney),
  subscriptionStartsOn: Type.Optional(NullableDate),
} as const;

export const LifelineCreateSchema = Type.Object(
  { operationId: OperationId, ...LifelineWriteFields },
  { additionalProperties: false },
);

export const LifelineUpdateSchema = Type.Object(
  {
    operationId: OperationId,
    version: Type.Integer({ minimum: 1 }),
    ...LifelineWriteFields,
  },
  { additionalProperties: false },
);

export type JobCreate = Static<typeof JobCreateSchema>;
export type JobCodePreviewQuery = Static<typeof JobCodePreviewQuerySchema>;
export type JobOwnerBalanceQuery = Static<typeof JobOwnerBalanceQuerySchema>;
export type JobUpdate = Static<typeof JobUpdateSchema>;
export type JobStatusTransition = Static<typeof JobStatusTransitionSchema>;
export type JobTimeEntryCreate = Static<typeof JobTimeEntryCreateSchema>;
export type JobTimeEntriesCreate = Static<typeof JobTimeEntriesCreateSchema>;
export type JobCostCreate = Static<typeof JobCostCreateSchema>;
export const JobCostUpdateSchema = Type.Object(
  { ...JobCostCreateSchema.properties, version: Type.Integer({ minimum: 1 }) },
  { additionalProperties: false },
);
export type JobRecoveryCreate = Static<typeof JobRecoveryCreateSchema>;
export type JobRecoveryTransfer = Static<typeof JobRecoveryTransferSchema>;
export type JobRecoveryMerge = Static<typeof JobRecoveryMergeSchema>;
export type JobMerge = Static<typeof JobMergeSchema>;
export type JobDeleteQuery = Static<typeof JobDeleteQuerySchema>;
export type JobEntryDeleteQuery = Static<typeof JobEntryDeleteQuerySchema>;
export type LifelineCreate = Static<typeof LifelineCreateSchema>;
export type LifelineUpdate = Static<typeof LifelineUpdateSchema>;

export type OperationsQuery = Static<typeof OperationsQuerySchema>;
export type PlanningWindowQuery = Static<typeof PlanningWindowQuerySchema>;
export type PlanningFeasibleUpdate = Static<
  typeof PlanningFeasibleUpdateSchema
>;
export type PlanningItemCreate = Static<typeof PlanningItemCreateSchema>;
export type PlanningItemUpdate = Static<typeof PlanningItemUpdateSchema>;
export type PlanningItemDeleteQuery = Static<
  typeof PlanningItemDeleteQuerySchema
>;
export type PlanningClear = Static<typeof PlanningClearSchema>;
export type PlanningWeekendUpdate = Static<typeof PlanningWeekendUpdateSchema>;
export type PlanningWarehouseCopyCreate = Static<
  typeof PlanningWarehouseCopyCreateSchema
>;

export interface JobSummary {
  id: string;
  managementYear: number;
  legacyUid: string | null;
  code: string;
  title: string;
  description: string | null;
  parentJobId: string | null;
  accountingMode: "primary" | "unresolved" | "rollup" | "independent";
  jobKind: "standard" | "recovery-x" | "lifeline" | "internal";
  status: string;
  classification: string | null;
  administratorName: string | null;
  clientName: string | null;
  condominiumName: string | null;
  commercialContact: string | null;
  clientId: string | null;
  condominiumId: string | null;
  administratorId: string | null;
  administratorClientId: string | null;
  openedOn: string | null;
  expectedEndOn: string | null;
  closedAt: string | null;
  workCompletedAt?: string | null;
  invoiceAuthorizedAt: string | null;
  quoteAmount: string | null;
  budgetAmount: string | null;
  expectedRevenue: string | null;
  budgetSource: "none" | "legacy" | "manual" | "quote";
  expectedRevenueSource: "none" | "legacy" | "manual" | "quote";
  workStatus: "open" | "in-progress" | "closed" | "recovery-x";
  /** Derived from work completion and verified FIC documents; never a manual flag. */
  isClosed?: boolean;
  closedWithoutBilling?: boolean;
  nonBillableClosedAt?: string | null;
  nonBillableCloseReason?: string | null;
  /** Financial stage after work completion, derived from FIC documents. */
  postWorkStatus?: "to-invoice" | "report" | "to-collect" | "closed" | null;
  billingComplete?: boolean;
  hasPendingFicAssociation?: boolean;
  hasPendingBillingDocument?: boolean;
  hasPaidAdvanceAwaitingFinal?: boolean;
  hasPartialPayment?: boolean;
  hasOverduePayment?: boolean;
  billingStatus: "not-invoiced" | "intervention-report" | "invoiced";
  billingSource:
    "none" | "intervention-report" | "legacy" | "fic" | "legacy-fic-mismatch";
  billingVerificationStatus:
    | "not-applicable"
    | "pending-fic"
    | "confirmed"
    | "amount-mismatch"
    | "not-found"
    | "manual-review";
  paymentStatus:
    | "not-invoiced"
    | "verification-required"
    | "to-collect"
    | "partial"
    | "collected"
    | "overdue";
  ownerEmployeeId: string | null;
  ownerName: string | null;
  fiscalClientName: string | null;
  fiscalTaxCode: string | null;
  fiscalVatNumber: string | null;
  fiscalVatId: number | null;
  fiscalVatPercent: string | null;
  fiscalWithholdingEnabled: boolean;
  receivedAmount: string | null;
  paymentDueDate: string | null;
  invoicedAmount: string;
  ficInvoicedAmount: string;
  legacyInvoicedAmount: string | null;
  collectedAmount: string;
  outstandingAmount: string;
  profitAmount: string;
  profitMargin: string | null;
  archiveStorageReference: string | null;
  archiveAbsolutePath: string | null;
  archiveDocumentCount: number;
  archiveHasFiles: boolean;
  planningFeasible: boolean;
  planningPlanned: boolean;
  planningFlagAvailable: boolean;
  laborHours: string;
  laborCost: string;
  directCost: string;
  supplierCost: string;
  totalOperationalCost: string;
  measurementUnit: string | null;
  plannedQuantity: string | null;
  actualQuantity: string | null;
  plannedHours: string | null;
  subjobCount: number;
  mergedIntoJobId: string | null;
  mergedIntoCode: string | null;
  mergedSourceCount: number;
  version: number;
  updatedAt: string;
}

export interface SubjobSummary extends JobSummary {
  subjobNumber: number;
}

export interface JobTimeEntry {
  baseHourlyCost?: string | null;
  internalHourlyAllocation?: string | null;
  employeeId?: string;
  recurringOccurrenceId?: string | null;
  id: string;
  jobId: string;
  jobCode: string;
  employeeName: string;
  workedOn: string;
  hours: string;
  hourlyCost: string;
  totalCost: string;
  accountingScope: "cem" | "personal";
  activity: string | null;
  source: "legacy" | "web" | "mobile" | "adjustment";
  deletable: boolean;
  version: number;
}

export interface JobCostEntry {
  supplierId?: string | null;
  vatStatus?: "known" | "unknown" | "not-applicable";
  netAmount?: string | null;
  vatAmount?: string | null;
  recurringOccurrenceId?: string | null;
  id: string;
  jobId: string;
  jobCode: string;
  kind: "supplier" | "generic";
  supplierName: string | null;
  incurredOn: string;
  category: string;
  description: string | null;
  amount: string;
  subcontract: boolean;
  deletable: boolean;
  version: number;
}

export interface JobRecovery {
  sourceCode: string | null;
  mergedIntoCode: string | null;
  mergedAmount: string | null;
  transferredAmount: string | null;
  residualAmount: string | null;
  accruedAmount: string;
  mergedInAmount: string;
  mergedOutAmount: string;
  currentTransferredAmount: string;
  currentResidualAmount: string;
  movements: JobRecoveryMovement[];
}

export interface JobRecoveryMovement {
  id: string;
  movementType: "accrual" | "transfer" | "merge";
  relatedJobId: string;
  relatedJobCode: string;
  occurredOn: string;
  amount: string;
  notes: string | null;
  createdAt: string;
}

export interface JobArchiveSummary {
  storageReference: string;
  declaredCreated: boolean;
  declaredHasFiles: boolean;
  verificationStatus: string;
  documentCount: number;
}

export interface JobDetail extends JobSummary {
  hasLinkedQuote?: boolean;
  commercialReservePercent?: string;
  commercialBaseAmount?: string | null;
  commercialReserveLocked?: boolean;
  informationNotes?: string | null;
  contactPhone?: string | null;
  subjobs: SubjobSummary[];
  timeEntries: JobTimeEntry[];
  costs: JobCostEntry[];
  recovery: JobRecovery | null;
  archive: JobArchiveSummary | null;
}

export interface JobPage {
  items: JobSummary[];
  nextCursor: string | null;
  managementYear: number;
  totalCount: number;
}

export interface JobCodePreview {
  code: string;
  administratorId: string;
  condominiumId: string;
  commercialContact: string | null;
}

export interface JobOwnerBalanceRow {
  employeeId: string;
  employeeName: string;
  jobCount: number;
  assignedValue: string;
  projectedJobCount: number;
  projectedAssignedValue: string;
  recommended: boolean;
}

export interface JobOwnerBalance {
  quoteAmount: string;
  recommendedEmployeeId: string | null;
  rows: JobOwnerBalanceRow[];
}

export interface PlanningAbsenceRequest {
  id: string;
  status: "pending" | "approved";
  allDay: boolean;
  /** Local Rome time; the end is exclusive, as in the employee app. */
  startLocal: string;
  endLocal: string;
}

export interface PlanningItem {
  absenceRequest?: PlanningAbsenceRequest | null;
  pausedOn?: string | null;
  pausedRemainingDays?: number | null;
  pausedRemainingHours?: number | null;
  recurringOccurrenceId?: string | null;
  recurringPlanId?: string | null;
  recurringVersion?: number | null;
  recurringConflict?: boolean;
  id: string;
  kind: "assignment" | "absence";
  version: number;
  employeeId: string | null;
  workerKind: "employee" | "subcontract";
  worker: string;
  workDate: string;
  durationDays: number;
  jobId: string | null;
  jobCode: string | null;
  jobTitle: string | null;
  condominiumName: string | null;
  administratorName: string | null;
  detail: string | null;
}

export interface WeekendWorkday {
  id: string;
  workDate: string;
  employeeId: string;
  worker: string;
  enabled: boolean;
  workValue: 0 | 0.5 | 1;
}

export interface PlanningWorker {
  id: string | null;
  name: string;
  kind: "employee" | "subcontract";
}

export interface PlanningWorkloadScope {
  id: string;
  remainingHours: number | null;
  needsReview: boolean;
  excludedInternal?: boolean;
}

export interface PlanningJob {
  /** Original job opening date; absent/null means it must be verified. */
  openedOn?: string | null;
  /** Estimated outstanding work; shared scopes are counted only once. */
  workload?: PlanningWorkloadScope[];
  lifelineControl?: {
    fascicleId: string;
    dueOn: string;
    scheduledOn: string | null;
    employeeId: string | null;
    ownerName: string | null;
    occurrenceId: string | null;
    occurrenceVersion: number | null;
  };
  id: string;
  code: string;
  title: string;
  description: string | null;
  condominiumName: string | null;
  administratorName: string | null;
  planningFeasible: boolean;
  warehouseCopyId: string | null;
  durationDays: number;
}

export interface PlanningJobState {
  id: string;
  version: number;
  planningFeasible: boolean;
  planningPlanned: boolean;
}

export interface PlanningWindow {
  from: string;
  to: string;
  workers: PlanningWorker[];
  warehouseJobs: PlanningJob[];
  items: PlanningItem[];
  pausedItems?: PlanningItem[];
  weekendWorkdays: WeekendWorkday[];
}

export interface LifelineSummary {
  id: string;
  code: string;
  title: string;
  condominiumName: string | null;
  administratorName: string | null;
  usabilityStatus: string;
  nextDeadline: string | null;
  openAnomalies: number;
  subscriptionStatus: string | null;
  address: string | null;
  responsible: string | null;
  lifecycleStatus: string;
  documentComplete: boolean;
  tettoSicuroActive: boolean;
  annualFeeAmount: string | null;
  lastInspectionOn: string | null;
  updatedAt: string;
  fascicleBacked: boolean;
}

export interface LifelinePage {
  items: LifelineSummary[];
  nextCursor: string | null;
}

export interface LifelineDeadline {
  id: string;
  deadlineType: string;
  dueOn: string;
  status: string;
  completedAt: string | null;
  notes: string | null;
}

export interface LifelineAnomaly {
  id: string;
  severity: string;
  description: string;
  status: string;
  detectedAt: string;
  resolvedAt: string | null;
  resolution: string | null;
}

export interface LifelineInspection {
  id: string;
  inspectionType: string;
  scheduledOn: string | null;
  performedOn: string | null;
  performedBy: string | null;
  outcome: string | null;
}

export interface LifelineSubscription {
  id: string;
  serviceType: string;
  startsOn: string;
  endsOn: string | null;
  status: string;
  billingFrequency: string | null;
  feeAmount: string | null;
  readyToInvoice: boolean;
}

export interface LifelineDocument {
  id: string;
  documentKind: string;
  fileName: string;
  mediaType: string | null;
  sizeBytes: string | null;
}

export interface LifelineDetail extends LifelineSummary {
  jobId: string | null;
  condominiumId: string | null;
  administratorId: string | null;
  clientName: string | null;
  jobCode: string | null;
  technicalData: Record<string, unknown>;
  manualFicCheckStatus: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  deadlines: LifelineDeadline[];
  anomalies: LifelineAnomaly[];
  inspections: LifelineInspection[];
  subscriptions: LifelineSubscription[];
  documents: LifelineDocument[];
}
